/**
 * Comparaison de l'offre annuelle au tarif mensuel.
 *
 * LE PRIX BARRÉ N'EST PAS UN ANCIEN PRIX. C'est l'équivalent au tarif mensuel — douze mensualités —
 * et c'est la seule chose qu'il ait le droit de dire. Un montant figé dans le code serait faux
 * partout où la monnaie n'est pas l'euro, et une réduction annoncée sur un prix jamais pratiqué
 * dans la devise de l'acheteur est une fausse réduction, attaquable comme telle.
 *
 * D'où la règle unique de ce module : le barré se dérive du prix RÉEL servi par le SDK, dans la
 * devise servie par le SDK — ou il ne s'affiche pas. Jamais d'approximation, jamais de repli sur
 * une valeur « à peu près juste ».
 */

/** Ce qu'on retient d'un package RevenueCat : le SDK formate déjà `priceString` pour la locale. */
export interface TarifPackage {
  price: number;
  priceString: string;
  currencyCode: string;
}

export interface OffreAnnuelle {
  /** Prix annuel, tel que formaté par le SDK. Toujours présent. */
  prixAnnuel: string;
  /** 12 × le mensuel, formaté dans la même devise. `null` = ne rien afficher. */
  prixBarre: string | null;
  /** Prix mensuel formaté, pour la mention « soit X/mois × 12 ». `null` avec `prixBarre`. */
  prixMensuel: string | null;
  /**
   * Remise en pourcentage sur douze mensualités, ARRONDIE VERS LE BAS — le badge « −16 % ». Calculée
   * sur les prix du store comme le barré, donc juste dans toute devise. `null` = pas de badge.
   */
  remise: number | null;
}

const MOIS_PAR_AN = 12;

/**
 * Devises dont le groupement N'EST PAS uniforme : trois chiffres, puis des paires. Au-delà de cent
 * mille, la roupie s'écrit 1,19,988 et non 119,988 — reproduire le séparateur du gabarit ne suffit
 * donc pas quand le montant à rendre change d'ordre de grandeur.
 *
 * Liste EXPLICITE plutôt que déduite : le système lakh/crore se reconnaît à la devise, pas à la
 * chaîne. Un gabarit sous cent mille ne le montre jamais — « ₹99,999.00 » se groupe exactement comme
 * en Occident.
 */
const GROUPEMENT_LAKH = new Set(['INR', 'PKR', 'BDT', 'NPR', 'LKR']);

/** Trois chiffres à droite, puis des paires. 119988 → 1,19,988. */
function grouperLakh(entier: string, sep: string): string {
  if (entier.length <= 3) return entier;
  const paires: string[] = [];
  let reste = entier.slice(0, -3);
  while (reste.length > 2) {
    paires.unshift(reste.slice(-2));
    reste = reste.slice(0, -2);
  }
  if (reste) paires.unshift(reste);
  return [...paires, entier.slice(-3)].join(sep);
}

/** Espaces employées comme séparateur de milliers : ordinaire, insécable, fine insécable. */
const ESPACES = /[ \u00A0\u202F]/;

/**
 * Rend `valeur` DANS LA CONVENTION D'UN MODÈLE, plutôt qu'avec une locale.
 *
 * POURQUOI PAS `Intl`. Le prix principal affiché est le `priceString` du store, tel quel — c'est ce
 * que la personne verra aussi sur sa fiche App Store. Reformater le barré avec la locale de
 * l'APPAREIL produisait deux conventions sur la même ligne dès que les deux divergent : « $129.99 »
 * au-dessus de « 155,88 US$ » sur un appareil français relié à une boutique américaine. Même
 * devise, deux écritures. Invisible en zone euro, bancal partout ailleurs.
 *
 * On se calque donc sur le `priceString` MENSUEL, seul porteur de la convention réelle du store :
 * symbole, sa place, séparateur décimal, séparateur de milliers, espacement. Le montant y est
 * SUBSTITUÉ ; rien n'est reformaté.
 *
 * `valeurModele` lève l'ambiguïté que la chaîne seule ne permet pas de trancher : « 1,560 » vaut
 * 1,56 ou 1560 selon la convention, et seul le montant numérique le dit.
 *
 * Rend `null` si le modèle n'est pas exploitable — on n'affiche alors aucun barré, plutôt qu'un
 * montant dans une convention inventée.
 */
export function calquerSurModele(
  modele: string,
  valeurModele: number,
  valeur: number,
  devise = '',
): string | null {
  const bloc = /[\d][\d.,\u00A0\u202F ]*\d|\d/.exec(modele);
  if (!bloc) return null;
  const chiffres = bloc[0];

  // Le séparateur DÉCIMAL est le dernier « . » ou « , » qui reconstitue `valeurModele`. Les autres
  // séparateurs, quels qu'ils soient, groupent les milliers.
  let decimales: number | null = null;
  let sepDecimal = '';
  for (let i = chiffres.length - 1; i >= 0; i -= 1) {
    const c = chiffres[i];
    if (c !== '.' && c !== ',') continue;
    const partieDecimale = chiffres.slice(i + 1);
    if (!/^\d{1,3}$/.test(partieDecimale)) break;
    const entier = chiffres.slice(0, i).replace(/[^\d]/g, '');
    if (Math.abs(Number(`${entier}.${partieDecimale}`) - valeurModele) < 0.005) {
      decimales = partieDecimale.length;
      sepDecimal = c;
    }
    break;
  }
  if (decimales === null) {
    // Aucun séparateur décimal : devise sans centime (yen), ou modèle illisible.
    if (Math.abs(Number(chiffres.replace(/[^\d]/g, '')) - valeurModele) >= 0.005) return null;
    decimales = 0;
  }

  // Séparateur de milliers : celui qui subsiste une fois le décimal écarté.
  const avantDecimal = sepDecimal ? chiffres.slice(0, chiffres.lastIndexOf(sepDecimal)) : chiffres;
  const sepMilliers = ESPACES.test(avantDecimal)
    ? (ESPACES.exec(avantDecimal) as RegExpExecArray)[0]
    : avantDecimal.includes(',') && sepDecimal !== ','
      ? ','
      : avantDecimal.includes('.') && sepDecimal !== '.'
        ? '.'
        : '';

  const [entier, frac] = valeur.toFixed(decimales).split('.');

  /**
   * LE GABARIT DOIT AVOIR DÉMONTRÉ LE GROUPEMENT QU'ON APPLIQUE.
   *
   * Un modèle sous mille n'en montre aucun : « 999,99 € » ne dit pas si les milliers se séparent par
   * une espace, un point ou rien. Extrapoler produirait « 1199,88 € » — un montant juste, écrit dans
   * une convention que le store n'emploie pas. On préfère ne rien barrer.
   *
   * En revanche, un modèle de plus de trois chiffres SANS séparateur a démontré quelque chose : ce
   * store ne groupe pas. On le suit.
   */
  const entierGabarit = chiffres.replace(/[^\d]/g, '').slice(0, decimales ? -decimales : undefined);
  if (entier.length > 3 && !sepMilliers && entierGabarit.length <= 3) return null;

  const groupe = !sepMilliers
    ? entier
    : GROUPEMENT_LAKH.has(devise.toUpperCase())
      ? grouperLakh(entier, sepMilliers)
      : entier.replace(/\B(?=(\d{3})+(?!\d))/g, sepMilliers);
  const rendu = frac ? `${groupe}${sepDecimal}${frac}` : groupe;

  // Substitution DANS le modèle : symbole, place et espacement sont conservés par construction.
  return modele.slice(0, bloc.index) + rendu + modele.slice(bloc.index + chiffres.length);
}

/**
 * Un `priceString` porte-t-il un montant lisible ? Se calquer sur lui-même le prouve.
 *
 * Sert à la MENTION « soit X/mois × 12 », qui reprend le mensuel verbatim : si sa chaîne n'expose
 * aucun montant exploitable, la phrase deviendrait « soit prix sur demande/mois × 12 ». Le barré,
 * lui, peut se calquer sur l'annuel et rester juste — les deux conditions sont donc distinctes.
 */
function lisible(tarif: TarifPackage): boolean {
  return calquerSurModele(tarif.priceString, tarif.price, tarif.price, tarif.currencyCode) !== null;
}

function exploitable(tarif: TarifPackage | null): tarif is TarifPackage {
  return (
    !!tarif &&
    Number.isFinite(tarif.price) &&
    tarif.price > 0 &&
    typeof tarif.currencyCode === 'string' &&
    tarif.currencyCode.length > 0
  );
}

export function comparerAuMensuel(annuel: TarifPackage, mensuel: TarifPackage | null): OffreAnnuelle {
  const seul: OffreAnnuelle = {
    prixAnnuel: annuel.priceString,
    prixBarre: null,
    prixMensuel: null,
    remise: null,
  };

  if (!exploitable(mensuel)) return seul;

  const equivalent = mensuel.price * MOIS_PAR_AN;

  // GARDE-FOU CONTRE LA FAUSSE RÉDUCTION À L'ENVERS : si l'annuel n'est pas moins cher que douze
  // mensualités, un barré serait INFÉRIEUR au prix affiché et annoncerait une économie inexistante.
  // Mieux vaut ne rien barrer que barrer à l'envers.
  if (!Number.isFinite(annuel.price) || annuel.price >= equivalent) return seul;

  /**
   * GABARIT : LE PRIX ANNUEL D'ABORD, le mensuel en repli.
   *
   * Les deux viennent du même store et portent donc la même convention — mais l'annuel est du MÊME
   * ORDRE DE GRANDEUR que l'équivalent qu'on rend, et lui seul révèle le séparateur de milliers
   * quand il y en a un. Un mensuel à « 299,00 kr » ne dit rien du groupement ; l'annuel
   * « 2 999,00 kr » le donne, et c'est précisément dans ce cas que l'équivalent le franchit.
   */
  const barre =
    calquerSurModele(annuel.priceString, annuel.price, equivalent, annuel.currencyCode) ??
    calquerSurModele(mensuel.priceString, mensuel.price, equivalent, mensuel.currencyCode);
  // Modèle inexploitable : ni barré ni mention, comme pour toute autre absence. Mieux vaut le seul
  // prix annuel qu'un montant écrit dans une convention que le store n'emploie pas.
  if (!barre) return seul;

  return {
    prixAnnuel: annuel.priceString,
    prixBarre: barre,
    // La mention n'apparaît que si le mensuel expose un montant lisible : sans quoi le barré reste
    // juste, calqué sur l'annuel, mais la phrase qui l'explique ne voudrait rien dire.
    prixMensuel: lisible(mensuel) ? mensuel.priceString : null,
    // Au centième près avant l'arrondi : 199,99 / 239,88 donne 16,6305…, mais un flottant juste sous
    // un entier (15,9999…) ne doit pas perdre un point qu'il a réellement.
    remise: Math.floor(Math.round((1 - annuel.price / equivalent) * 10_000) / 100),
  };
}
